// ═══════════════════════════════════════════════════════════════
// GeoOps — Spatial geometry utilities
// ═══════════════════════════════════════════════════════════════

import L from 'leaflet';

const R_EARTH = 6371000; // meters
const DEG2RAD = Math.PI / 180;

/** Haversine distance in meters */
export function haversineDistance(
  lat1: number, lng1: number, lat2: number, lng2: number
): number {
  const dLat = (lat2 - lat1) * DEG2RAD;
  const dLng = (lng2 - lng1) * DEG2RAD;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLng / 2) ** 2;
  return R_EARTH * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Point-in-polygon test using ray casting */
export function isPointInPolygon(
  lat: number, lng: number, polygon: number[][]
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if (((yi > lng) !== (yj > lng)) &&
      (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Check if a point is inside a GeoJSON geometry */
export function isInsideZone(
  lat: number, lng: number, geometry: GeoJSON.Geometry
): boolean {
  if (!geometry) return false;

  if (geometry.type === 'Polygon') {
    // GeoJSON coordinates are [lng, lat], convert to [lat, lng] for our test
    const ring = geometry.coordinates[0].map(([gLng, gLat]) => [gLat, gLng]);
    return isPointInPolygon(lat, lng, ring);
  }

  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.some(poly => {
      const ring = poly[0].map(([gLng, gLat]) => [gLat, gLng]);
      return isPointInPolygon(lat, lng, ring);
    });
  }

  if (geometry.type === 'Point') {
    // Circle encoded as Point with radius in properties (handled by caller)
    return false;
  }

  return false;
}

/** Calculate polygon area in square meters using Shoelace formula on projected coords */
export function polygonArea(geometry: GeoJSON.Geometry): number {
  if (geometry.type !== 'Polygon') return 0;
  const coords = geometry.coordinates[0];
  if (coords.length < 3) return 0;

  // Project to approximate meters using center point
  const cLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const latScale = R_EARTH * DEG2RAD;
  const lngScale = R_EARTH * DEG2RAD * Math.cos(cLat * DEG2RAD);

  let area = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const x1 = coords[i][0] * lngScale;
    const y1 = coords[i][1] * latScale;
    const x2 = coords[i + 1][0] * lngScale;
    const y2 = coords[i + 1][1] * latScale;
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area) / 2;
}

/** Area in square miles */
export function polygonAreaSqMi(geometry: GeoJSON.Geometry): number {
  return polygonArea(geometry) / 2589988; // sq meters to sq miles
}

/** Get Leaflet bounds for a GeoJSON geometry */
export function zoneBounds(geometry: GeoJSON.Geometry): L.LatLngBounds | null {
  const points: L.LatLngExpression[] = [];

  if (geometry.type === 'Polygon') {
    geometry.coordinates[0].forEach(([lng, lat]) => points.push([lat, lng]));
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(poly =>
      poly[0].forEach(([lng, lat]) => points.push([lat, lng]))
    );
  } else if (geometry.type === 'LineString') {
    geometry.coordinates.forEach(([lng, lat]) => points.push([lat, lng]));
  } else if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates;
    points.push([lat, lng]);
  }

  if (points.length === 0) return null;
  return L.latLngBounds(points);
}

/** Convert circle (center + radius) to a GeoJSON Polygon with N segments */
export function circleToPolygon(
  lat: number, lng: number, radiusMeters: number, segments = 64
): GeoJSON.Polygon {
  const coords: number[][] = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const dLat = (radiusMeters / R_EARTH) * Math.cos(angle) * (180 / Math.PI);
    const dLng = (radiusMeters / R_EARTH) * Math.sin(angle) * (180 / Math.PI) / Math.cos(lat * DEG2RAD);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: 'Polygon', coordinates: [coords] };
}

/** Length of a LineString in meters (sum of segment haversine distances) */
export function polylineLength(geometry: GeoJSON.Geometry): number {
  if (geometry.type !== 'LineString') return 0;
  const coords = geometry.coordinates;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += haversineDistance(coords[i][1], coords[i][0], coords[i + 1][1], coords[i + 1][0]);
  }
  return total;
}

/** Length of an in-progress polyline given lat/lng vertices */
export function polylineLengthFromVertices(vertices: [number, number][]): number {
  let total = 0;
  for (let i = 0; i < vertices.length - 1; i++) {
    total += haversineDistance(vertices[i][0], vertices[i][1], vertices[i + 1][0], vertices[i + 1][1]);
  }
  return total;
}

/** Buffer a LineString by N meters using turf — returns a Polygon corridor */
export async function bufferLine(line: GeoJSON.LineString, radiusMeters: number): Promise<GeoJSON.Polygon | null> {
  const { default: buffer } = await import('@turf/buffer');
  const { lineString } = await import('@turf/helpers');
  try {
    const f = lineString(line.coordinates);
    const buffered = buffer(f, radiusMeters / 1000, { units: 'kilometers', steps: 16 });
    if (!buffered) return null;
    const g = (buffered as GeoJSON.Feature).geometry;
    if (g.type === 'Polygon') return g;
    if (g.type === 'MultiPolygon') return { type: 'Polygon', coordinates: g.coordinates[0] };
    return null;
  } catch {
    return null;
  }
}

// ── Pretty-print measurements ──────────────────────────────────

/** Distance in meters → human-readable ("123 ft" / "0.4 mi") */
export function formatDistance(meters: number): string {
  if (!isFinite(meters) || meters <= 0) return '0 ft';
  const feet = meters * 3.28084;
  if (feet < 528) return `${Math.round(feet)} ft`;          // < 0.1 mi
  const miles = feet / 5280;
  if (miles < 10) return `${miles.toFixed(2)} mi`;
  return `${miles.toFixed(1)} mi`;
}

/** Area in square meters → human-readable ("4,500 sq ft" / "2.3 acres" / "1.2 sq mi") */
export function formatArea(sqMeters: number): string {
  if (!isFinite(sqMeters) || sqMeters <= 0) return '0 sq ft';
  const sqFt = sqMeters * 10.7639;
  if (sqFt < 10000) return `${Math.round(sqFt).toLocaleString()} sq ft`;
  const acres = sqMeters / 4046.86;
  if (acres < 640) return `${acres.toFixed(acres < 10 ? 2 : 1)} acres`;
  const sqMi = sqMeters / 2589988;
  return `${sqMi.toFixed(sqMi < 10 ? 2 : 1)} sq mi`;
}

/** Get centroid of a geometry */
export function geometryCentroid(geometry: GeoJSON.Geometry): [number, number] | null {
  let lats: number[] = [];
  let lngs: number[] = [];

  if (geometry.type === 'Polygon') {
    geometry.coordinates[0].forEach(([lng, lat]) => { lats.push(lat); lngs.push(lng); });
  } else if (geometry.type === 'LineString') {
    geometry.coordinates.forEach(([lng, lat]) => { lats.push(lat); lngs.push(lng); });
  } else if (geometry.type === 'Point') {
    return [geometry.coordinates[1], geometry.coordinates[0]];
  } else {
    return null;
  }

  if (lats.length === 0) return null;
  return [
    lats.reduce((a, b) => a + b, 0) / lats.length,
    lngs.reduce((a, b) => a + b, 0) / lngs.length,
  ];
}
